/**
 * Structured client-side logger for the web app.
 *
 * Mirrors the server's `{ ts, level, msg, ... }` JSON shape so logs
 * forwarded to a future log sink (Sentry, OTLP, BigQuery via GA4)
 * can be parsed with the same shape pipeline. Scope is prefixed on
 * every line so a `grep "scope":"sse"` filter works in DevTools
 * alongside future server-side aggregation.
 *
 * Why a wrapper instead of bare `console.error`:
 *   1. Consistency — today the codebase has six different prefix
 *      conventions (`[reflecto-web] x failed:`, `[webStorage] ...`,
 *      `useStorage: ...`, `PairingPage: ...`, etc.). One helper
 *      collapses these to a single `{ scope, msg }` axis.
 *   2. Pluggability — error reporting (Sentry) is now wired: see the
 *      `logError` forwarding below.
 *   3. Rule compliance — the project's security rules forbid logging
 *      notification plaintext or secret material. Centralising the
 *      sink makes it trivial to grep call sites for accidental
 *      leakage and to add a future redaction pass.
 *
 * The helper does NOT throw, NOT return anything, and NOT do any
 * I/O beyond `console.error` / `console.warn` (plus the Sentry
 * forward described below). Callers can `void` it from anywhere —
 * including inside React render paths — without worrying about side
 * effects.
 */

import { captureError } from "./observability";

export type LogScope =
  | "sse"
  | "messages"
  | "notifications"
  | "pairing"
  | "storage"
  | "ui"
  | "clipboard";

interface LogPayload {
  ts: string;
  level: "info" | "warn" | "error";
  scope: LogScope;
  msg: string;
  err?: string;
  /** Free-form structured context. Stays flat for grep-ability. */
  [key: string]: unknown;
}

function buildPayload(
  level: "info" | "warn" | "error",
  scope: LogScope,
  msg: string,
  err?: unknown,
  extra?: Record<string, unknown>
): LogPayload {
  // `extra` is merged FIRST so the fixed diagnostic fields below
  // always win on any key collision. Otherwise a caller passing
  // `{ msg: "..." }` or `{ scope: "..." }` would silently overwrite
  // the structured shape every log consumer depends on.
  const payload: LogPayload = {
    ...(extra ?? {}),
    ts: new Date().toISOString(),
    level,
    scope,
    msg,
  };
  if (err !== undefined) {
    payload.err = err instanceof Error ? err.message : String(err);
  } else {
    // When the caller passed no Error, strip any `extra.err` that
    // sneaked in via the spread above so the fixed-field-wins
    // invariant holds unconditionally for `err` too.
    delete payload.err;
  }
  return payload;
}

/**
 * Emit an error-level structured log line. Use for failures the
 * user couldn't have caused themselves (network timeouts, decrypt
 * failures, storage write errors). Does NOT include stack traces
 * by default — error reporting wiring will add them when it lands.
 *
 * Also forwards to Sentry via `captureError` — errors only (not
 * `logWarn`/`logInfo`; the free Sentry tier's 5K-events/mo quota
 * doesn't stretch to every warn/info line). When `err` is already an
 * `Error`, it's forwarded as-is so the stack trace survives; a defined
 * non-`Error` `err` (a string, a decode failure) is wrapped in a fresh
 * `Error` built from `${scope}: ${msg}` so Sentry still gets a real
 * exception object. A call with NO `err` at all does not forward —
 * message-only logError lines are local diagnostics, not exceptions,
 * and forwarding them would burn quota on non-errors.
 * The `extra` context is deliberately NEVER forwarded — it's free-form
 * application data that hasn't passed the scrubber, and `captureError`
 * takes no payload parameter by design (see `lib/observability.ts`);
 * the only safe control here is not passing it at all.
 */
export function logError(
  scope: LogScope,
  msg: string,
  err?: unknown,
  extra?: Record<string, unknown>
): void {
  console.error(JSON.stringify(buildPayload("error", scope, msg, err, extra)));
  if (err instanceof Error) {
    captureError(err);
  } else if (err !== undefined) {
    captureError(new Error(`${scope}: ${msg}`));
  }
}

/**
 * Emit a warn-level structured log line. Use for recoverable
 * conditions where the app is still functional (transient poll
 * failures, malformed SSE event swallowed by validation, missing
 * optional capability).
 */
export function logWarn(
  scope: LogScope,
  msg: string,
  err?: unknown,
  extra?: Record<string, unknown>
): void {
  console.warn(JSON.stringify(buildPayload("warn", scope, msg, err, extra)));
}

/**
 * Emit an info-level structured log line. Use for happy-path
 * milestones that need to be distinguishable from failure logs
 * in production diagnostics (notification stored, sync round
 * completed). Mirrors the extension's `debug()` posts.
 */
export function logInfo(scope: LogScope, msg: string, extra?: Record<string, unknown>): void {
  console.info(JSON.stringify(buildPayload("info", scope, msg, undefined, extra)));
}
